import { numberToPrettyString } from '@cian/utils';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { PriceRequestForm } from 'shared/components/PriceRequestForm';
import { useChats } from 'shared/hooks/useChats';
import { isFromBuilderSelector, selectUser } from 'shared/selectors';
import { selectEarlyAccessContactsFlowEnabled } from 'shared/selectors/earlyAccess/selectEarlyAccessContactsFlowEnabled';
import { getAgent } from 'shared/selectors/newObject/getAgent';
import { offerChatSelector } from 'shared/selectors/offerChat';
import { selectAgentLists } from 'shared/selectors/offerData/selectAgentLists';
import { EStatusOffer } from 'shared/types/offerData';

import { DISCOUNT_PRICE } from './constants';
import { trackPriceRequestClick } from './tracking';
import { useEarlyAccessPaywallContext } from '../../../packages/EarlyAccessPaywall';
import {
  EAccessCheckTrackingStatus,
  ESubscriptionLimitType,
} from '../../../packages/EarlyAccessPaywall/shared/types/context';
import { trackBlockedEarlyAccessChatClick } from '../../utils/tracking/earlyAccessTracking';
import { selectOffer } from '../AuthorAsideBrandContainer/selectors/selectors';
import { trackSendMessageClick } from '../ChatPopupContainer/tracking';

export const PriceRequestContainer: React.FC = () => {
  const [value, setValue] = React.useState<number>(0);
  const { isChatsEnabled } = useSelector(offerChatSelector);
  const user = useSelector(selectUser);
  const offer = useSelector(selectOffer);
  const agent = useSelector(getAgent);
  const isFromBuilder = useSelector(isFromBuilderSelector);
  const isEarlyAccessContactsFlowEnabled = useSelector(selectEarlyAccessContactsFlowEnabled);
  const { handleOpenChat } = useChats();
  const { checkContactsAccess } = useEarlyAccessPaywallContext();

  const price =
    offer.priceTotalRur ||
    offer.priceTotalPerMonthRur ||
    offer.pricePerUnitAreaRur ||
    offer.bargainTerms.prices.rur ||
    0;

  const isOfferDaily = offer.category.includes('daily');
  const isOfferPublished = offer.status === EStatusOffer.Published;

  const agentLists = useSelector(selectAgentLists);

  const isAuctionAgentLists = React.useMemo(
    () => agentLists.includes('tradeRealty') || agentLists.includes('electronTradeSite'),
    [agentLists],
  );

  const discountPrice = React.useMemo(
    () => numberToPrettyString(Math.floor(Math.floor(price * DISCOUNT_PRICE) / 100) * 100),
    [price],
  );

  const handleEarlyAccessFlow = React.useCallback(
    async (messageText: string) => {
      if (!user?.isAuthenticated) {
        trackBlockedEarlyAccessChatClick({
          offer,
          agent,
          blockType: EAccessCheckTrackingStatus.Unauthorized,
        });
      }

      const { isConfirmed, trackingStatus } = await checkContactsAccess({
        offerId: offer.id,
        limitType: ESubscriptionLimitType.Chat,
      });

      if (isConfirmed) {
        trackPriceRequestClick(offer, value);
        trackSendMessageClick({ offer, agent, isEarlyAccess: true });
        handleOpenChat({ defaultMessage: messageText, shouldSendDefaultMessage: true, isEarlyAccess: true });

        return;
      }

      if (trackingStatus) {
        trackBlockedEarlyAccessChatClick({
          offer,
          agent,
          blockType: trackingStatus,
        });
      }
    },
    [checkContactsAccess, offer, agent, user?.isAuthenticated, handleOpenChat, value],
  );

  const handleSend = React.useCallback(async () => {
    if (value) {
      const messageText = `Здравствуйте! Вы не против небольшого торга? Предлагаю цену ${numberToPrettyString(
        value,
      )}? Вы готовы сделать скидку?`;

      if (isEarlyAccessContactsFlowEnabled) {
        await handleEarlyAccessFlow(messageText);

        return;
      }

      trackPriceRequestClick(offer, value);
      handleOpenChat({ defaultMessage: messageText, shouldSendDefaultMessage: true });
    }
  }, [handleOpenChat, handleEarlyAccessFlow, isEarlyAccessContactsFlowEnabled, offer, value]);

  const handleChange = React.useCallback((value: number) => {
    setValue(value);
  }, []);

  if (
    isFromBuilder ||
    isAuctionAgentLists ||
    isOfferDaily ||
    !isChatsEnabled ||
    user?.cianUserId === offer.cianUserId ||
    !isOfferPublished
  ) {
    return null;
  }

  return (
    <PriceRequestForm
      discountPrice={discountPrice}
      onClick={handleSend}
      onChange={handleChange}
      title={'Предложите свою цену'}
    />
  );
};
